import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "@phosphor-icons/react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { clsx } from "clsx";
import { kfDrawerBottomIn, kfDrawerLeftIn, kfDrawerRightIn, kfOverlayIn } from "@/lib/emotionTw";
import { IconButton } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import type { DrawerPlacement, DrawerProps, DrawerSize } from "./Drawer.types";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Note: the pre-migration bottom placement passed
 * the unprefixed (dead) class "w-full" as its size — no size rule applied, so
 * bottom placement contributes no size styles here either. Verified against
 * the frozen baseline. */

const drawerPlacementStyles: Record<DrawerPlacement, CSSObject> = {
  right: {
    right: 0,
    top: 0,
    height: "100dvh",
    ...kfDrawerRightIn,
    animation: "bfrs-bfrs-drawer-right-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  left: {
    left: 0,
    top: 0,
    height: "100dvh",
    ...kfDrawerLeftIn,
    animation: "bfrs-bfrs-drawer-left-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  bottom: {
    bottom: 0,
    left: 0,
    maxHeight: "85dvh",
    width: "100%",
    borderTopLeftRadius: "var(--bfrs-radius-xl)",
    borderTopRightRadius: "var(--bfrs-radius-xl)",
    ...kfDrawerBottomIn,
    animation: "bfrs-bfrs-drawer-bottom-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  }
};

const drawerSizeStyles: Record<DrawerSize, CSSObject> = {
  sm: { width: "100%", maxWidth: "360px" },
  md: { width: "100%", maxWidth: "28rem" },
  lg: { width: "100%", maxWidth: "48rem" },
  xl: { width: "100%", maxWidth: "min(1400px,75vw)" }
};

const DrawerOverlay = styled(RadixDialog.Overlay, {
  label: "AgenticDrawer-overlay"
})({
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 50,
  backgroundColor: "hsl(var(--bfrs-color-text-primary) / .3)",
  ...kfOverlayIn,
  animation: "bfrs-bfrs-overlay-in var(--bfrs-motion-fast) var(--bfrs-ease-out)"
});

interface DrawerContentOwnerState {
  placement: DrawerPlacement;
  size: DrawerSize;
}

const DrawerContent = styled(RadixDialog.Content, {
  label: "AgenticDrawer-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: DrawerContentOwnerState }>(({ ownerState }) => [
  {
    position: "fixed",
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-overlay)",
    "--tw-shadow-colored": "var(--bfrs-shadow-overlay)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    outline: "2px solid transparent",
    outlineOffset: "2px"
  },
  ownerState.placement === "bottom" ? { borderTopWidth: "1px" } : { borderLeftWidth: "1px" },
  drawerPlacementStyles[ownerState.placement],
  ownerState.placement !== "bottom" && drawerSizeStyles[ownerState.size]
]);

const DrawerHeader = styled("div", {
  label: "AgenticDrawer-header"
})({
  display: "flex",
  alignItems: "flex-start",
  gap: "1rem",
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

const DrawerHeaderText = styled("div", {
  label: "AgenticDrawer-headerText"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const DrawerTitle = styled(RadixDialog.Title, {
  label: "AgenticDrawer-title"
})({
  fontSize: "1rem",
  lineHeight: "1.5rem",
  fontWeight: 700,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const DrawerDescription = styled(RadixDialog.Description, {
  label: "AgenticDrawer-description"
})({
  marginTop: ".25rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const DrawerBody = styled("div", {
  label: "AgenticDrawer-body"
})({
  minHeight: "0px",
  flex: "1 1 0%",
  overflow: "auto",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

const DrawerFooter = styled("div", {
  label: "AgenticDrawer-footer"
})({
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

export function Drawer({
  open,
  defaultOpen,
  onOpenChange,
  onClose,
  trigger,
  title,
  description,
  children,
  footer,
  placement = "right",
  size = "md",
  closeButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  preventClose = false,
  className,
  contentClassName,
  headerClassName,
  footerClassName
}: DrawerProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && preventClose) return;
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  };

  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <BfrsPortalScope>
          <DrawerOverlay />
          <DrawerContent
            ownerState={{ placement, size }}
            className={clsx(className, contentClassName)}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick || preventClose) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape || preventClose) event.preventDefault();
            }}
          >
            {(title || description || closeButton) && (
              <DrawerHeader className={headerClassName}>
                <DrawerHeaderText>
                  {title && <DrawerTitle>{title}</DrawerTitle>}
                  {description && <DrawerDescription>{description}</DrawerDescription>}
                </DrawerHeaderText>
                {closeButton && (
                  <RadixDialog.Close asChild>
                    <IconButton aria-label="Close drawer" icon={<X size={16} />} variant="outline" size="sm" disabled={preventClose} />
                  </RadixDialog.Close>
                )}
              </DrawerHeader>
            )}
            <DrawerBody>{children}</DrawerBody>
            {footer && <DrawerFooter className={footerClassName}>{footer}</DrawerFooter>}
          </DrawerContent>
        </BfrsPortalScope>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
